import React, { useEffect, useState } from "react";
import api, { formatApiError, formatTime, formatDate, formatPrice } from "../lib/api";
import StatusChip from "./StatusChip";
import { X, Plus, Check } from "lucide-react";

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal placeholder:text-slate focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";

/**
 * Modal for practitioners to log a completed visit (notes + tinctures + last-minute add-ons).
 */
export default function LogVisitDialog({ open, appointmentId, onClose, onSaved }) {
  const [appt, setAppt] = useState(null);
  const [notes, setNotes] = useState("");
  const [tinctures, setTinctures] = useState([]);
  const [status, setStatus] = useState("completed");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Add-ons: catalog of services that can be tacked on + new rows the practitioner is staging
  const [addOnCatalog, setAddOnCatalog] = useState([]);
  const [newAddOns, setNewAddOns] = useState([]); // [{ service_id?, name, price, notes }]
  const existingAddOns = appt?.add_ons || [];

  useEffect(() => {
    if (!open || !appointmentId) return;
    setNotes(""); setTinctures([]); setStatus("completed"); setError(""); setNewAddOns([]);
    api.get("/appointments", {}).then(({ data }) => {
      const found = data.find((a) => a.id === appointmentId);
      setAppt(found || null);
    });
    api.get("/services", { params: { only_active: true } }).then(({ data }) => {
      // All services are eligible add-ons — practitioners may charge for anything.
      // Sort: add-ons first (most common at point-of-sale), then bookable.
      const sorted = [...data].sort((a, b) => {
        if (!!a.is_add_on === !!b.is_add_on) return a.name.localeCompare(b.name);
        return a.is_add_on ? -1 : 1;
      });
      setAddOnCatalog(sorted);
    }).catch(() => {});
  }, [open, appointmentId]);

  const addTincture = () => setTinctures([...tinctures, { product_name: "", quantity: "", expected_duration_days: 30 }]);
  const updateTincture = (i, f, v) => setTinctures(tinctures.map((t, idx) => idx === i ? { ...t, [f]: v } : t));
  const removeTincture = (i) => setTinctures(tinctures.filter((_, idx) => idx !== i));

  const addAddOnRow = () => setNewAddOns([...newAddOns, { service_id: "", name: "", price: "" }]);
  const updateAddOn = (i, patch) => setNewAddOns(newAddOns.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const removeAddOnRow = (i) => setNewAddOns(newAddOns.filter((_, idx) => idx !== i));
  const pickCatalog = (i, svcId) => {
    const svc = addOnCatalog.find((s) => s.id === svcId);
    if (svc) updateAddOn(i, { service_id: svc.id, name: svc.name, price: Number(svc.price || 0).toFixed(2) });
    else updateAddOn(i, { service_id: "", name: "", price: "" });
  };
  const removeExistingAddOn = async (id) => {
    await api.delete(`/appointments/${appointmentId}/add-ons/${id}`);
    const { data } = await api.get("/appointments", {});
    setAppt(data.find((a) => a.id === appointmentId) || appt);
  };

  const save = async () => {
    setSubmitting(true); setError("");
    try {
      // 1) Save the new add-ons first so they're persisted even if completion fails
      for (const row of newAddOns) {
        if (!row.name && !row.service_id) continue;
        const price = parseFloat(row.price);
        await api.post(`/appointments/${appointmentId}/add-ons`, {
          service_id: row.service_id || null,
          name: row.name || addOnCatalog.find((s) => s.id === row.service_id)?.name || "Add-on",
          price: Number.isFinite(price) ? price : 0,
          notes: row.notes || "",
        });
      }
      // 2) Complete the appointment
      await api.post(`/appointments/${appointmentId}/complete`, {
        operational_notes: notes,
        tinctures: tinctures.map((t) => ({
          product_name: t.product_name,
          quantity: t.quantity,
          expected_duration_days: t.expected_duration_days ? Number(t.expected_duration_days) : null,
        })),
        status,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  const totalAddOns = [...existingAddOns, ...newAddOns]
    .reduce((sum, a) => sum + (Number(a.price) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 animate-fade-in" data-testid="log-visit-dialog">
      <div className="bg-white border border-hairline rounded-xl shadow-warm-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-sage">Log visit</p>
            <h2 className="font-display text-xl text-charcoal mt-1">Complete this appointment</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate hover:text-forest rounded-md" aria-label="Close" data-testid="log-visit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && <div className="px-3 py-2 bg-status-danger-bg text-status-danger text-sm font-ui rounded-md" data-testid="log-visit-error">{error}</div>}

          {appt && (
            <div className="bg-white border border-hairline rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-ui text-sm text-charcoal">
                    {appt.client ? `${appt.client.first_name} ${appt.client.last_initial}.` : "—"}
                    <span className="text-slate"> · </span>
                    {appt.service?.name}
                  </p>
                  <p className="text-xs font-ui text-slate mt-0.5">{formatDate(appt.scheduled_start)} · {formatTime(appt.scheduled_start)} – {formatTime(appt.scheduled_end)}</p>
                </div>
                <StatusChip status={appt.status} />
              </div>
            </div>
          )}

          <div>
            <span className="block text-xs font-ui text-slate mb-1.5 flex items-center gap-1.5">Outcome <span className="text-sage text-xs" aria-label="required">•</span></span>
            <div className="inline-flex bg-white border border-hairline rounded-pill p-1">
              {[
                { k: "completed", label: "Completed" },
                { k: "no_show", label: "No-show" },
                { k: "canceled", label: "Cancelled" },
              ].map(({ k, label }) => (
                <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1 text-xs font-ui rounded-pill ${status === k ? "bg-charcoal text-white" : "text-slate"}`} data-testid={`log-status-${k}`}>{label}</button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="block text-xs font-ui text-slate mb-1">Operational notes</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} data-testid="log-visit-notes" />
          </label>

          {status === "completed" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-ui text-slate">Tinctures recommended</span>
                <button onClick={addTincture} className="text-xs font-ui text-sage hover:text-forest inline-flex items-center gap-1" data-testid="log-add-tincture">
                  <Plus className="w-3 h-3" />Add
                </button>
              </div>
              <div className="space-y-2">
                {tinctures.map((t, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`log-tincture-row-${i}`}>
                    <input placeholder="Product" value={t.product_name} onChange={(e) => updateTincture(i, "product_name", e.target.value)} className={`${inputCls} col-span-5`} data-testid={`log-tincture-name-${i}`} />
                    <input placeholder="Quantity (e.g. 1 oz)" value={t.quantity} onChange={(e) => updateTincture(i, "quantity", e.target.value)} className={`${inputCls} col-span-4`} data-testid={`log-tincture-qty-${i}`} />
                    <input type="number" placeholder="Days" value={t.expected_duration_days} onChange={(e) => updateTincture(i, "expected_duration_days", e.target.value)} className={`${inputCls} col-span-2`} data-testid={`log-tincture-days-${i}`} />
                    <button onClick={() => removeTincture(i)} className="col-span-1 text-slate hover:text-status-danger"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {tinctures.length === 0 && <p className="text-xs font-ui text-slate">None — add if you're recommending a refill schedule.</p>}
              </div>
            </div>
          )}

          {/* Add-ons section */}
          <div data-testid="log-addons-section">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-ui text-slate">Last-minute add-ons {totalAddOns > 0 && <span className="text-forest font-medium">· {formatPrice(totalAddOns)}</span>}</span>
              <button onClick={addAddOnRow} className="text-xs font-ui text-sage hover:text-forest inline-flex items-center gap-1" data-testid="log-add-addon">
                <Plus className="w-3 h-3" />Add service
              </button>
            </div>

            {existingAddOns.length > 0 && (
              <div className="space-y-1 mb-2">
                {existingAddOns.map((ao) => (
                  <div key={ao.id} className="flex items-center justify-between px-3 py-2 bg-cream/40 border border-hairline rounded-md" data-testid={`existing-addon-${ao.id}`}>
                    <div>
                      <span className="text-sm font-ui text-charcoal">{ao.name}</span>
                      <span className="text-xs text-slate ml-2">· {formatPrice(ao.price)}</span>
                    </div>
                    <button onClick={() => removeExistingAddOn(ao.id)} className="text-slate hover:text-status-danger" aria-label="Remove"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              {newAddOns.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`new-addon-row-${i}`}>
                  <select
                    value={row.service_id}
                    onChange={(e) => pickCatalog(i, e.target.value)}
                    className={`${inputCls} col-span-7`}
                    data-testid={`new-addon-service-${i}`}
                  >
                    <option value="">— Pick a service or type custom name →</option>
                    {addOnCatalog.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.is_add_on ? "+ " : ""}{s.name} — {formatPrice(s.price)}
                      </option>
                    ))}
                  </select>
                  <div className="col-span-4 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-sm pointer-events-none">$</span>
                    <input
                      type="number" inputMode="decimal" step="0.01" min="0"
                      placeholder="0.00"
                      value={row.price}
                      onChange={(e) => updateAddOn(i, { price: e.target.value })}
                      className={`${inputCls} pl-7`}
                      data-testid={`new-addon-price-${i}`}
                    />
                  </div>
                  <button onClick={() => removeAddOnRow(i)} className="col-span-1 text-slate hover:text-status-danger" aria-label="Remove"><X className="w-4 h-4" /></button>
                  {row.service_id === "" && (
                    <input
                      placeholder="Custom service name (e.g. Wellness Coaching)"
                      value={row.name}
                      onChange={(e) => updateAddOn(i, { name: e.target.value })}
                      className={`${inputCls} col-span-11 col-start-1`}
                      data-testid={`new-addon-name-${i}`}
                    />
                  )}
                </div>
              ))}
              {existingAddOns.length === 0 && newAddOns.length === 0 && (
                <p className="text-xs font-ui text-slate italic">Saw them in office? Add a Parasite Cleanse, extra allergen, sauna pass — anything that needs billing.</p>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-hairline flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-ui text-slate hover:text-forest" data-testid="log-visit-cancel">Cancel</button>
          <button onClick={save} disabled={submitting} className="px-5 py-2.5 bg-forest hover:bg-charcoal disabled:opacity-40 text-white rounded-md text-sm font-medium font-ui inline-flex items-center gap-2 transition-colors" data-testid="log-visit-save">
            <Check className="w-4 h-4" /> {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
